/**
 * Mount-INDEPENDENT connection-lifecycle eviction for the Object Explorer's
 * query cache.
 *
 * Every `['database-objects', *, connectionId]` entry belongs to ONE connect. A
 * disconnect, a delete, or a Force Reconnect that rebuilds a connection in place
 * ends that connect, and the rows it cached must not survive it — most visibly
 * the shared database listing (`['database-objects','databases',connectionId]`),
 * which every picker paints from before its own revalidation lands. With the
 * listing retained indefinitely (`gcTime: Infinity`, see `useDatabaseList`),
 * "the entry is dropped when the connect ends" is the ONLY thing bounding it.
 *
 * This used to live exclusively in `DatabaseExplorer.endConnectionGeneration`,
 * which only runs while the Explorer is mounted — and the Explorer unmounts
 * whenever the sidebar's explorer pane is collapsed (`SidebarContent` renders it
 * behind `shouldShowExplorer`; Ctrl+Shift+S and maximising the Scripts panel
 * both flip it). Collapse the Explorer, edit a connection's server or
 * credentials, reconnect, and the previous endpoint's database list stayed
 * cached and was served to every picker.
 *
 * So the eviction is derived from the connection store itself, from every CONNECT
 * BOUNDARY an id crosses:
 *
 *   1. the connection left `activeConnections` (disconnect that removes it, or
 *      Delete Connection) — whatever its status was before;
 *   2. its status became `'disconnected'`;
 *   3. it stayed live but got a NEW `connectedAt` — Force Reconnect rebuilding
 *      the same id in place;
 *   4. it became LIVE — a first connect, or a reconnect out of `'disconnected'`.
 *
 * (4) is the other half of the retention contract: a fresh connect must start from
 * an EMPTY slot. Retention is `gcTime: Infinity`, so anything that reached the slot
 * outside a live connect would otherwise be served to the connect that follows —
 * and what usually reaches it is a FAILURE (`NO_POOL`, stale credentials), read
 * back through the Explorer's own `fetchQuery`. The write side is closed too
 * (`databaseListSharing.canShareDatabaseListing` routes a not-live listing off the
 * shared key); (4) is what makes the guarantee hold regardless — for entries
 * written before that rule existed, and for any producer that bypasses it.
 * (1) drops its `before.live` precondition for the same reason: deleting a saved
 * profile that was ALREADY disconnected was invisible to this subscriber, so its
 * entries were retained for the session with nothing left that could drop them.
 *
 * The Explorer keeps calling `endConnectionGeneration` for the bookkeeping only
 * IT owns (generation fence, preload wave queue, `isPreloading`); the query
 * cancel/remove half is shared with this module through
 * {@link evictConnectionObjectQueries}, so the two can never drift on the key
 * predicate. Running twice is harmless — both halves are idempotent.
 */

import type { QueryClient } from '@tanstack/react-query';

import { useConnectionStore } from '../../connections/store/connectionStore';
import type { ActiveConnection } from '../../connections/models/connectionTypes';
import { isLiveConnectStatus } from './databaseListSharing';

/**
 * Cancel and remove every Object Explorer query belonging to a connection.
 *
 * Not optional, and not just about staleness: `useDatabaseQueries` keys object
 * listings by `(objectType, connectionId, database)` alone, so a successor
 * connect asking for the same key would JOIN the predecessor's in-flight query
 * and publish ITS rows. Cancelling and removing (rather than versioning the key)
 * keeps ordinary refresh caching exactly as it was.
 */
export function evictConnectionObjectQueries(
  queryClient: QueryClient,
  connectionId: string,
): void {
  const belongsToConnection = (queryKey: readonly unknown[]): boolean =>
    queryKey[0] === 'database-objects' && queryKey[2] === connectionId;
  void queryClient.cancelQueries({ predicate: (q) => belongsToConnection(q.queryKey) });
  queryClient.removeQueries({ predicate: (q) => belongsToConnection(q.queryKey) });
}

/**
 * The identity of the connect a connection is currently on. `live: false` is a
 * present-but-disconnected entry; `connectedAt` is `undefined` while the store
 * has not stamped one yet (a connect still dialling, a restored entry).
 */
type ConnectIdentity = { live: boolean; connectedAt: number | undefined };

export type ConnectionLifecycleSnapshot = ReadonlyMap<string, ConnectIdentity>;

type LifecycleInput = ReadonlyArray<
  Pick<ActiveConnection, 'id' | 'status'> & { connectedAt?: Date | number }
>;

const connectedAtMs = (value: Date | number | undefined): number | undefined => {
  const ms = value instanceof Date ? value.getTime() : value;
  return typeof ms === 'number' && Number.isFinite(ms) ? ms : undefined;
};

export function snapshotConnectionLifecycle(
  activeConnections: LifecycleInput | undefined,
): ConnectionLifecycleSnapshot {
  const snapshot = new Map<string, ConnectIdentity>();
  for (const connection of activeConnections ?? []) {
    snapshot.set(connection.id, {
      // ONE definition of live, shared with the sharing rule that decides who may
      // write the shared listing slot — see `databaseListSharing`.
      live: isLiveConnectStatus(connection.status),
      connectedAt: connectedAtMs(connection.connectedAt),
    });
  }
  return snapshot;
}

/**
 * The connections that crossed a CONNECT BOUNDARY between two snapshots — the
 * eviction trigger set. The four boundaries are enumerated in the module comment;
 * `DatabaseExplorer`'s own effects react to the first three.
 *
 * Deliberately NOT a boundary: any tick where an id's liveness, presence and
 * `connectedAt` are all unchanged. `knownDatabasesTracker` publishes a new
 * `activeConnections` array ~1s after every connect, which is exactly when the
 * user first opens a picker — evicting there would throw away the rows the connect
 * just fetched. A connection that merely gained its FIRST `connectedAt` stamp is
 * the same connect for the same reason.
 */
export function connectionsAtConnectBoundary(
  prev: ConnectionLifecycleSnapshot,
  next: ConnectionLifecycleSnapshot,
): string[] {
  const crossed: string[] = [];
  for (const [connectionId, before] of prev) {
    const after = next.get(connectionId);
    // GONE — a disconnect that removes the entry, or Delete Connection. Reported
    // whatever `before.live` was: a saved profile deleted while already
    // disconnected still has to take its cached rows with it.
    if (!after) {
      crossed.push(connectionId);
      continue;
    }
    // Liveness flipped, EITHER way. Down ends the connect; up starts a new one,
    // which must not inherit anything (see the module comment on boundary 4).
    if (before.live !== after.live) {
      crossed.push(connectionId);
      continue;
    }
    // A REPLACED connect (Force Reconnect in place): same id, still live, new
    // `connectedAt`. Only comparable when both sides carry one.
    if (
      before.live &&
      before.connectedAt !== undefined &&
      after.connectedAt !== undefined &&
      before.connectedAt !== after.connectedAt
    ) {
      crossed.push(connectionId);
    }
  }
  // A connection that appears already LIVE — the ordinary first connect of a
  // session, and the shape a reconnect takes when the disconnect removed the entry
  // rather than flipping its status.
  for (const [connectionId, after] of next) {
    if (!prev.has(connectionId) && after.live) crossed.push(connectionId);
  }
  return crossed;
}

/**
 * Subscribe to the connection store and evict at every connect boundary. Returns
 * the unsubscribe; each tick is diffed against zustand's own `prevState`, so
 * starting and stopping repeatedly (a React StrictMode double-mount) holds no
 * state that could go stale.
 *
 * Ordering matters for the newly-live boundary and holds by construction: zustand
 * runs subscribers synchronously inside `set()`, while the Explorer's own
 * on-connect listing is a React effect. The eviction therefore always lands before
 * the connect's first fetch, never on top of its result.
 */
export function startConnectionListingLifecycle(queryClient: QueryClient): () => void {
  return useConnectionStore.subscribe((state, prevState) => {
    if (state.activeConnections === prevState.activeConnections) return;
    const crossed = connectionsAtConnectBoundary(
      snapshotConnectionLifecycle(prevState.activeConnections),
      snapshotConnectionLifecycle(state.activeConnections),
    );
    for (const connectionId of crossed) {
      evictConnectionObjectQueries(queryClient, connectionId);
    }
  });
}
